/* eslint max-classes-per-file: ["error", 2] */

import {debounceTime, startWith, map, distinctUntilChanged} from 'rxjs/operators';
import {Component, Input, OnInit, OnDestroy, OnChanges, Output, EventEmitter} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {AddressModel} from './address.model';
import {FmcElementsComponent} from '../fmc-elements.component';
import {checkStateType, checkStreetType, noWhitespaceValidator, InvalidSiblingFormControlValidator} from '../common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {AsyncPipe, NgFor, NgIf} from '@angular/common';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatSelectModule} from '@angular/material/select';
import { fmcValidator } from '../common';

@Component({
  selector: 'fmc-address-search',
  templateUrl: './address-search.component.html',
  styleUrls: ['./address-search.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    AsyncPipe
  ]
})
export class AddressSearchComponent extends FmcElementsComponent implements OnInit, OnDestroy, OnChanges {

  static SEARCH_MODES = {
    INTERNAL: 'internal',
    EXTERNAL: 'external'
  }

  @Input() public required = true; // addresses are required by default
  @Input() public readonly = false;
  @Input() public addressInvalid = false;
  @Input() public placeholderText: string;
  @Input() public controlName: string;
  @Input() public parentFormGroup: UntypedFormGroup;
  @Input() public fieldName: string;
  @Input() public allowPOBox = true;
  @Input() public toggleResetFormValues = true; // dictates whether the values are reset when switching between manual/search
  @Input() public allowTba = false;
  @Input() public relatedAddresses: any = [];
  @Input() public allowPropertyType = false;
  @Input() public mode = AddressSearchComponent.SEARCH_MODES.INTERNAL;
  @Input() public nonAuPostCodeRequired = true;
  @Output() public relatedAddress = new EventEmitter<any>();

  public defaultControl = new AddressModel().addressForm;
  public defaultControlType = 'FormGroup';
  public showManualAddress = false;
  public filteredAddress: any;
  public selectedManualStreetType: any;
  public formGroup: UntypedFormGroup;
  public streetTypes: any = [];
  public propertyTypes: any = [];
  public countries: any = [];

  public auStates: any = [];

  public filteredSuburbs: any = [];
  public filteredStates: any = [];
  public filteredStreetTypes: Observable<any[]>;
  public filteredPropertyTypes: Observable<any[]>;
  public auCountryCode = 13;
  public readonlyState = true;
  public fullAdress = '';
  public currentSearchStr = '';
  public currentIsManualEntry = null;
  public currentIsPoBox = null;

  public vxStreetTypes: any[] = [];
  public vxCountries: any[] = [];
  public vxStates: any[] = [];

  public subscriptions = [];
  public startRender = false;

  async ngOnInit() {
    super.ngOnInit();
    console.log('ngOnInit', this.controlName);
    const initFormVal = this.formGroup.value;
    if (!this.formGroup.get('country').value) {
      // if `country` is empty, then set the default value as AU country.
      this.formGroup.get('country').patchValue(this.auCountryCode, {
        emitEvent: false
      });
    }

    await this.populateTypes();

    this.currentIsManualEntry = this.formGroup.get('isManualEntry').value;
    this.currentIsPoBox = this.formGroup.get('isPoBox').value;
    this.changeValidatorSettings(this.currentIsManualEntry, this.currentIsPoBox, false);

    this.subscriptions.push(this.formGroup.valueChanges.pipe(debounceTime(500),distinctUntilChanged()).subscribe((val) => {
      this.updateFullAddress(val);
    }));

    this.subscriptions.push(this.formGroup.get('isManualEntry').valueChanges.subscribe((isManualEntry) => {
      if (isManualEntry !== this.currentIsManualEntry) {
        this.currentIsManualEntry = isManualEntry;
        this.changeValidatorSettings(isManualEntry, this.formGroup.get('isPoBox').value, true);
      }
    }));

    this.subscriptions.push(this.formGroup.get('isPoBox').valueChanges.subscribe((isPoBox) => {
      if (isPoBox !== this.currentIsPoBox) {
        this.currentIsPoBox = isPoBox;
        this.changeValidatorSettings(this.formGroup.get('isManualEntry').value, isPoBox, true);
      }
    }));


    // fix for loading of addresses without proper values
    if (!initFormVal.isManualEntry && initFormVal.flatSearch && !initFormVal.postCode) {
      if (initFormVal.flatSearch !== 'TBA') {
        this.findAddress(initFormVal.flatSearch, false);
      }
    }

    // set default flat search
    this.currentSearchStr = (!this.formGroup.value.isManualEntry) ? this.formGroup.value.flatSearch : null;
    this.subscriptions.push(this.formGroup.get('flatSearch').valueChanges.pipe(debounceTime(500)).subscribe((searchStr) => {
      console.log('searchStr', searchStr);

      if (searchStr && searchStr !== 'TBA') {
        console.log('findAddress', searchStr);
        this.findAddress(searchStr);
      } else if (this.isTBA(searchStr)) {
        this.changeValidatorSettings(this.currentIsManualEntry, this.currentIsPoBox, false, false);
      }
    }));

    this.subscriptions.push(this.formGroup.get('suburb').valueChanges.pipe(debounceTime(500)).subscribe((searchStr) => {
      this.findSuburb();
    }));

    this.subscriptions.push(this.formGroup.get('streetTypeSearch').valueChanges.pipe(debounceTime(500)).subscribe((streetType) => {
      this.initializeStreetTypes(streetType);
      console.log('formGroup', this.formGroup);
    }));

    this.filteredStreetTypes = this.formGroup.get('streetTypeSearch').valueChanges
      .pipe(
        startWith(''),
        map(streetType => streetType ? this.filterStreetTypes(streetType) : this.streetTypes.slice())
      );

    this.subscriptions.push(
      this.formGroup.get('state')
      .valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe((stateId) => {
        if (stateId) {
          this.findState();
          this.updateStateId(stateId);
        }
      })
    );


      this.subscriptions.push(
        this.formGroup.get('stateId')
          .valueChanges
          .pipe(
            debounceTime(500),
            distinctUntilChanged()
          ).subscribe((stateId) => {
            console.log('State ID changed', stateId, this.formGroup.get('stateId').value);
            if (!stateId) {
              // if stateId is null, undefined, or empty string, then do nothing.
              return;
            }
      this.updateStateId(stateId);

      if (!this.required && this.formGroup) {
        console.log('clear address validators')
        this.formGroup.get('flatSearch').setErrors(null);
        this.formGroup.clearValidators();
        this.formGroup.updateValueAndValidity();
        console.log('this.formGroup', this.formGroup)
      }
    }));

    this.subscriptions.push(
      this.formGroup.get('country')
        .valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged()
        ).subscribe((country) => {
      // console.log('Country ID changed', country);
          this.filteredStates = this.filterVXStates(country);
          const match = this.vxCountries.find(c => c.ext.ID === country);
          if (match) {
            this.formGroup.get('countrySlug').patchValue(match.id);
          } else {
            console.log('No match found for countryID: ' + country);
          }
          this.updatePostcodeValidation(country);
      })
    );


    this.filteredStates = this.filterVXStates();
    this.readonlyState = this.isAuCountryCode();
    this.updatePostcodeValidation();

    // set to disabled if readonly
    if (this.readonly) {
      this.formGroup.get('flatSearch').disable();
    }

    if (this.allowPropertyType) {
      this.subscriptions.push(this.formGroup.get('propertyTypeSearch').valueChanges.pipe(debounceTime(500)).subscribe((propertyType) => {
        if (typeof propertyType === 'string') {
          propertyType = {
            id: propertyType,
          };
        }

        const match = this.propertyTypes.find((property) => property.id === propertyType.id);

        if (match) {
          this.formGroup.get('propertyTypeSearch').patchValue(match, { emitEvent: false });
          this.formGroup.get('propertyType').patchValue(match.id, { emitEvent: false });
          this.formGroup.get('propertyTypeSearch').setErrors(null);
        } else {
          this.formGroup.get('propertyTypeSearch').setErrors({ invalid: true });
        }
      }));
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges() {
    if (!this.required && this.formGroup) {
      console.log('clear address validators')
      this.formGroup.clearValidators();
      this.formGroup.updateValueAndValidity();
      this.formGroup.setErrors(null);
    }
  }

  changeValidatorSettings(isManualEntry, isPoBox, resetValue, emitEvent = true) {
    console.log('changeValidatorSettings');
    console.log('readonly', this.readonly);
    const poBoxFields = ['poBox', 'suburb', 'state', 'postcode'];
    const manualEntryFields = ['streetNumber', 'streetName', 'streetType', 'streetTypeSearch', 'suburb', 'state', 'stateId', 'postcode'];
    const autoSearchFields = ['flatSearch', 'suburb', 'state', 'postcode'];

    const clearValues = (control) => {
      if (resetValue && this.toggleResetFormValues) {
        this.formGroup.get(control).reset();
      }
      this.formGroup.get(control).clearValidators();
      this.formGroup.get(control).clearAsyncValidators();
      this.formGroup.get(control).updateValueAndValidity({emitEvent});
    };

    const setAsRequired = (control) => {
      if (control ==='postcode') {
        this.updatePostcodeValidation();
      } else {
        const validators = [
          Validators.required
        ];
        if (control === 'flatSearch') {
          validators.push(InvalidSiblingFormControlValidator([control]));
        } else if (control !== 'streetTypeSearch') {
          validators.push(noWhitespaceValidator);
        }
        this.formGroup.get(control).setValidators(Validators.compose(validators));
        this.formGroup.get(control).updateValueAndValidity({emitEvent});
      }
    };

    if (isManualEntry) {
      this.showManualAddress = true;

      if (this.toggleResetFormValues) {
        autoSearchFields.forEach(control => {
          clearValues(control);
        });
      }

      if (isPoBox) {
        this.formGroup.clearValidators();
        this.formGroup.setValidators(Validators.compose([checkStateType('stateId')]));

        manualEntryFields.forEach(control => {
          clearValues(control);
        });
        poBoxFields.forEach(control => {
          setAsRequired(control);
        });
      } else {
        this.formGroup.setValidators(Validators.compose([checkStateType('stateId'), checkStreetType('streetType')]));
        poBoxFields.forEach(control => {
          clearValues(control);
        });
        manualEntryFields.forEach(control => {
          setAsRequired(control);
        });
      }

    } else {
      const isTBA = this.isTBA();
      // if search populated, remove all validators
      // clear the validators on all manual entry fields
      this.formGroup.clearValidators();

      poBoxFields.forEach(control => {
        clearValues(control);
      });
      manualEntryFields.forEach(control => {
        clearValues(control);
      });
      // if TBA is allowed and flatSearch value is "TBA",
      //   then set only the `flatSearch` formcontrol to be required.
      autoSearchFields
        .filter((c) => isTBA ? (c === 'flatSearch') : true)
        .forEach(control => {
          setAsRequired(control);
        });
      this.showManualAddress = false;
    }
    this.formGroup.updateValueAndValidity({emitEvent: false});
  }

  updatedSelectedAddressRecord(address) {
    console.log('updatedSelectedAddressRecord', address);
    if (!address) return; // ignore blank address values

    let addressId = null
    let rpId = null
    let rank = null

    console.log('address', address);
    if (address && address.Address) {
      addressId = address.Address.iD;
    } else if (address && address.addressId) {
      addressId = address.addressId;
    } else if (address.iD) {
      addressId = address.iD;
    }

    if (address && address.Address && address.Address.rPDataPropertyID) {
      rpId = address.Address.rPDataPropertyID;
    } else if (address && address.rpId) {
      rpId = address.rpId;
    } else if (address && address.rPDataPropertyID) {
      rpId = address.rPDataPropertyID;
    } else if (address && address.propertyId) {
      rpId = address.propertyId;
    }

    if (address && address.Address && address.Address.rank) {
      rank = address.Address.rank
    } else if (address && address.rank) {
      rank = address.rank
    }

    this.formGroup.get('addressId').patchValue(addressId, { emitEvent: false });
    this.formGroup.get('rPDataPropertyID').patchValue(rpId, { emitEvent: false });
    this.formGroup.get('rPDataRank').patchValue(rank, { emitEvent: false });
    // this.formGroup.get('rPDataPropertyID').patchValue(address.id[0]);
    this.formGroup.get('flatSearch').setErrors(null, { emitEvent: false });
    // this.formGroup.get('flatSearch').markAsTouched();
    this.formGroup.get('flatSearch').updateValueAndValidity({ emitEvent: false });
    this.populateAddressFieldsFromSelected(addressId, rpId);
  }

  populateAddressFieldsFromSelected(id, rpId) {
    console.log('[populateAddressFieldsFromSelected]', id, this.mode);
    const search = (this.mode === AddressSearchComponent.SEARCH_MODES.INTERNAL)
      ? this.lookupService.getAddressByAid(id)
      : this.lookupService.getAddressByRpId(rpId);

    search
      .subscribe(async (result: any) => {
        if (result) {
          result = (this.mode === AddressSearchComponent.SEARCH_MODES.INTERNAL) ? result : result.data;

          // make sure postcode / suburb / state are not blank
          if (!result || !result.postCode || !result.suburb) {
            if (this.mode === 'external') {
              await this.lookupService.getAddressByRpId(rpId).subscribe((data: any) => {
                if (!data || !data.postCode || !data.suburb) {
                  console.log('Error during address details retrieval', result);
                  this.notificationService.notify('warn', 'Error retrieving Address Details. Please try again later or input address manually.');
                  this.formGroup.get('flatSearch').setErrors({ invalid: true }, { emitEvent: false });
                  this.formGroup.get('flatSearch').reset('', { emitEvent: false });
                  return;
                } else {
                  this.patchAddressResult(data);
                }
              });
            } else {
              console.log('Error during address details retrieval', result);
              this.notificationService.notify('warn', 'Error retrieving Address Details. Please try again later or input address manually.');
              this.formGroup.get('flatSearch').setErrors({ invalid: true }, { emitEvent: false });
              this.formGroup.get('flatSearch').reset('', { emitEvent: false });
              return;
            }
          } else {
            this.patchAddressResult(result);
          }
        }
      },
        error => {
          console.log('Error during address details retrieval', error);
          this.notificationService.notify('warn', 'Error retrieving Address Details. Please try again later or input address manually.');
          this.formGroup.get('flatSearch').reset('', { emitEvent: false });
          this.formGroup.get('flatSearch').setErrors({ invalid: true }, { emitEvent: false });
        });
  }

  patchAddressResult(result) {
    this.formGroup.get('country').patchValue(result['countryId'] || null);
    this.formGroup.get('postcode').patchValue(result['postCode'] || null);
    this.formGroup.get('propertyName').patchValue(result['propertyName'] || null);
    this.formGroup.get('streetName').patchValue(result['streetName'] || null);
    this.formGroup.get('streetNumber').patchValue(result['streetNumber'] || null);
    this.formGroup.get('streetType').patchValue(result['streetTypeId'] || null);
    this.formGroup.get('suburb').patchValue(result['suburb'] || null);
    this.formGroup.get('unitNumber').patchValue(result['unitNumber'] || null);

    if (result['suburb']) {
      this.updatedSelectedSuburb(result['suburb']);
    }

    if (result['streetTypeId']) {
      this.initializeStreetTypes({ id: result['streetTypeId'] });
    }

    if (result['stateId']) {
      this.updateStateId(result['stateId']);
    } else {
      console.log('stateId cleared');
      this.formGroup.get('state').patchValue(result['stateName'] || '');
      this.formGroup.get('stateId').patchValue(result['stateId'] || '');
    }

    this.updateFullAddress(result);
  }

  findAddress(searchStr, resetValue: boolean = true) {
    // do nothing if this was populated from RPData
    if (searchStr && searchStr === this.currentSearchStr) {
      // console.log('search string not changed');
      return;
    }
    if (!this.formGroup.value.isManualEntry) {
      // do not search if an object was selected

      if (searchStr && searchStr.text) {
        this.currentSearchStr = searchStr.text;
        this.formGroup.get('flatSearch').patchValue(searchStr.text, { emitEvent: false });
        return;
      }

      if ((searchStr === null || searchStr === undefined || searchStr.length === 0) && !this.required) { // do not search for blank addresses
        return;
      }

      if (this.mode === AddressSearchComponent.SEARCH_MODES.EXTERNAL) {
        if (resetValue && this.toggleResetFormValues) {
          // this.resetAddressValues(false);
        }
        this.formGroup.get('flatSearch').markAsTouched();
      }

      this.currentSearchStr = searchStr;


      const search = (this.mode === AddressSearchComponent.SEARCH_MODES.INTERNAL)
        ? this.lookupService.addressLookupInternal(searchStr)
        : this.lookupService.addressLookupExternal(searchStr);

      search
        .subscribe(result => {
          this.filteredAddress = (result) ? result.map(this.addressMapper(this.mode)) : [];
          // find exact string if possible
          // do this logic only for external searches
          if (this.mode === AddressSearchComponent.SEARCH_MODES.EXTERNAL) {
            const match = this.filteredAddress.filter(a => a.text === this.currentSearchStr);
            if (match.length === 1) {
              // if exact match is found
              if (this.toggleResetFormValues) {
                console.log('resetAddressValues');
                this.resetAddressValues(false);
              }
              this.updatedSelectedAddressRecord(match[0])
            }
          }

        }, err => {
          console.log('Error during address search.', err);
          // this.formGroup.get('flatSearch').markAsTouched();
          this.formGroup.get('flatSearch').setErrors({ invalid: true }, { emitEvent: false });
          this.formGroup.get('flatSearch').updateValueAndValidity({ emitEvent: false });
        });
    } else {
      if ((!this.filteredAddress || this.filteredAddress.length === 0) && !this.formGroup.value.isManualEntry) {
        this.formGroup.get('flatSearch').setErrors({
          invalid: true
        }, { emitEvent: false });
      }
      // this.formGroup.get('flatSearch').markAsTouched();
    }
  }

  displayText(object) {
    return (object && object.text) ? object.text : (object && object.addressText) ? object.addressText : (object && object.Address && object.Address.addressText) ? object.Address.addressText : object;
  }

  addressMapper(mode) {
    return (result) => {
      // console.log('ancillary address data', result);
      if (mode === AddressSearchComponent.SEARCH_MODES.INTERNAL) {
        return {
          id: result.ID,
          addressId: result.ID,
          text: result.AddressText,
          rpId: result.RPDataPropertyID,
          rank: result.Rank,
        };
      } else {
        return {
          id: result.id[0],
          addressId: null,
          text: result.address[0],
          rpId: result.id[0]
        };
      }
    };
  }

  toggleTbaAddress() {
    this.formGroup.reset();
    this.formGroup.get('flatSearch').patchValue('TBA', { emitEvent: false });
    this.formGroup.get('flatSearch').clearValidators();
    this.formGroup.get('flatSearch').updateValueAndValidity();
    this.formGroup.markAsDirty();
    //  to remove all of the `required` validation when address is set to manual then change to TBA.
    this.changeValidatorSettings(false, this.formGroup.get('isPoBox').value, false);
  }

  selectRelatedAddress(ev) {
    console.log('selectRelatedAddress', ev);
    this.relatedAddress.emit(ev);
  }

  toggleManualAddress(state) {
    if (!state && this.fullAdress && !this.formGroup.get('flatSearch').value) {
      this.formGroup.get('flatSearch').patchValue(this.fullAdress, { emitEvent: false });
    }

    if (this.toggleResetFormValues) {
      console.log('resetAddressValues');
      this.resetAddressValues();
    }

    if (state) {
      this.formGroup.get('country').patchValue(this.auCountryCode);
    }

    this.formGroup.get('isManualEntry').patchValue(state);
    this.changeValidatorSettings(state, this.formGroup.get('isPoBox').value, true)
    this.showManualAddress = state;

    if (!this.readonly) {
      this.formGroup.enable();
    }
  }



  initializeStreetTypes(streetType) {
    if (streetType) {
      let updateFullAddress = false
      // ## DEVNOTE ##
      // still will be able to support streetType string value and streetTypeId value
      if ((typeof streetType === 'string') || (typeof streetType === 'number')) {
        streetType = {
          id: streetType
        }
        updateFullAddress = true
      }
      if (streetType.id) {
        const match = this.vxStreetTypes.find(s => {
          return (s.ext.ID === streetType.id) || s.id === streetType.id;
        });

        console.log('streetType match', match);

        if (match) {
          this.formGroup.get('streetTypeSlug').patchValue(match.id);
          this.formGroup.get('streetType').patchValue(match.ext.ID);
          this.formGroup.get('streetTypeSearch').patchValue(match, { emitEvent: false});
          this.formGroup.get('streetTypeSearch').setErrors(null, { emitEvent: false});
          this.formGroup.get('streetTypeSearch').updateValueAndValidity({ emitEvent: false});
        } else {
          this.formGroup.get('streetTypeSearch').setErrors({invalid: true});
        }
        this.updateFullAddress(this.formGroup.value, true);
      }
    }
  }

  filterStreetTypes(streetType: any) {
    if (typeof streetType === 'string') {
      return this.streetTypes.filter(street =>
        street.text.toLowerCase().indexOf(streetType.toLowerCase()) === 0);
    }
  }

  displayStreetType(streetType?: any) {
    return streetType ? streetType.text : undefined;
  }

  findSuburb() {
    if (this.formGroup.value.isManualEntry && this.isAuCountryCode(this.formGroup.get('country').value) && this.formGroup.get('suburb').value) {
      const query = encodeURIComponent(this.formGroup.get('suburb').value);
      this.lookupService.suburbLookup(query)
        .subscribe(result => {
          this.filteredSuburbs = (result.data) ? result.data : [];
        });
    }
  }

  updatedSelectedSuburb(suburb) {

    const suburbName = (suburb.name) ? suburb.name : this.formGroup.get('suburb').value;
    const postcode = (suburb.postcode) ? suburb.postcode : this.formGroup.get('postcode').value;
    const state = (suburb.state) ? suburb.state : this.formGroup.get('state').value;

    this.formGroup.get('suburb').patchValue(suburbName, {emitEvent: false});
    this.formGroup.get('postcode').patchValue(postcode);
    this.formGroup.get('state').patchValue(state);
    this.updateStateId(state);
  }

  findState() {
    if (this.formGroup.value.isManualEntry && !this.isAuCountryCode(this.formGroup.get('country').value) && this.formGroup.get('state').value) {
      const query = this.formGroup.get('state').value.toLowerCase();
      this.filteredStates = this.filterVXStates(this.formGroup.get('country').value).filter((vxState) => vxState.ext.Name.toLowerCase().includes(query));
      // if manual entry, make sure state is one of the valid ones
      if (this.filteredStates.length !== 1 && this.formGroup.get('isManualEntry').value) {
        // allow state to be free text
        /* const stateText = this.formGroup.get('state').value || '';
        const stateError = stateText.trim().length === 0 ? null : {invalid: true};
        this.formGroup.get('state').setErrors(stateError);
        this.formGroup.get('state').updateValueAndValidity({emitEvent: false}); */
        console.log('stateId cleared');
        this.formGroup.get('stateId').patchValue(null, { emitEvent: false });
        this.formGroup.get('stateSlug').patchValue(null, { emitEvent: false });

      }
    }
  }

  updateStateId(stateIdentifier, emitEvent = false) {
    if (stateIdentifier) {
      const match = this.filteredStates.find((s) =>
        s.ext.Name === stateIdentifier ||
        s.ext.ID === stateIdentifier ||
        s.id === stateIdentifier);
      if (match) {
        this.formGroup.get('state').patchValue(match.ext.Name, { emitEvent });
        this.formGroup.get('stateId').patchValue(match.ext.ID, { emitEvent });
        this.formGroup.get('stateSlug').patchValue(match.id, { emitEvent });
      }
    }
  }

  displayState(filteredStates) {
    // console.log('here', filteredStates);
    return (stateId) => {
      // console.log('here2', stateId, filteredStates);
      if (filteredStates && filteredStates.length > 0) {
        const state = filteredStates.find((s) => s.id = stateId);
        return state ? state.ext.Name : undefined;
      }
      return undefined;
    };
  }

  updateSelectedStreet(value) {
    this.selectedManualStreetType = value;
  }

  updateSelectedCountry(countryId) {
    this.formGroup.get('state').patchValue('');
    console.log('stateId cleared');
    this.formGroup.get('stateId').patchValue(null);
    this.formGroup.get('stateSlug').patchValue(null);
    this.formGroup.get('postcode').patchValue('');
    this.formGroup.get('suburb').patchValue('');
    if (!this.isAuCountryCode(countryId)) {
      this.readonlyState = null;
      this.filteredSuburbs = [];
      this.formGroup.get('state').clearValidators();
      this.formGroup.get('state').updateValueAndValidity();
      this.formGroup.get('stateId').clearValidators();
      this.formGroup.get('stateId').updateValueAndValidity();
      this.formGroup.get('streetType').clearValidators();
      this.formGroup.get('streetType').updateValueAndValidity();
      this.formGroup.get('streetTypeSearch').clearValidators();
      this.formGroup.get('streetTypeSearch').updateValueAndValidity();
      this.formGroup.clearValidators();
      this.formGroup.updateValueAndValidity();

      console.log('this.formGroup', this.formGroup);
    } else {
      this.readonlyState = true;
      this.changeValidatorSettings(this.currentIsManualEntry, this.formGroup.get('isPoBox').value, true);
      this.formGroup.setValidators(Validators.compose([checkStateType('stateId'), checkStreetType('streetType')]));
    }
  }

  updateFullAddress(data, forceUpdateFlatSearch: boolean = false) {
    let streetTypeString = this.streetTypes.find(x => x.id === data['streetType'] || x.ext.ID === data['streetType']);

    let tempAddress = '';
    tempAddress += data['propertyName'] ? data['propertyName'].toString().trim() + ' ' : '';
    tempAddress += data['poBox'] ? 'P.O. Box ' + data['poBox'].toString().trim() + ' ' : '';
    tempAddress += data['unitNumber'] ? data['unitNumber'].toString().trim() + '/' : '';
    tempAddress += data['streetNumber'] ? data['streetNumber'].toString().trim() + ' ' : '';
    tempAddress += data['streetName'] ? data['streetName'].toString().trim() + ' ' : '';
    tempAddress += streetTypeString ? streetTypeString.text.toString().trim() + ' ' : '';
    tempAddress += data['suburb'] ? data['suburb'].toString().trim() + ' ' : '';
    tempAddress += data['state'] ? data['state'].toString().trim() + ' ' : '';
    tempAddress += data['postcode'] ? data['postcode'].toString().trim() : '';

    if (tempAddress !== this.fullAdress) {
      this.fullAdress = tempAddress.replace(/ +/g, ' ');
      this.formGroup.get('fullAddress').patchValue(this.fullAdress, { emitEvent: false });

      if (data['isManualEntry'] || forceUpdateFlatSearch) {
        this.formGroup.get('flatSearch').patchValue(this.fullAdress, { emitEvent: false });
      }
      this.formGroup.get('flatSearch').updateValueAndValidity({ emitEvent: false });
    }

    // console.log('[Address updated]', this.controlName, this.formGroup.value);
  }

  resetAddressValues(resetSearch = true) {
    this.fullAdress = '';
    this.formGroup.get('country').patchValue(this.auCountryCode);
    this.formGroup.get('postcode').patchValue(null);
    this.formGroup.get('propertyName').patchValue(null);
    this.formGroup.get('state').patchValue(null);
    console.log('stateId cleared');
    this.formGroup.get('stateId').patchValue(null);
    this.formGroup.get('stateSlug').patchValue(null);
    this.formGroup.get('streetName').patchValue(null);
    this.formGroup.get('streetNumber').patchValue(null);
    this.formGroup.get('streetType').patchValue(null);
    this.formGroup.get('streetTypeSlug').patchValue(null);
    this.formGroup.get('streetTypeSearch').patchValue(null);
    this.formGroup.get('addressId').patchValue(null);
    this.formGroup.get('rPDataPropertyID').patchValue(null);
    this.formGroup.get('suburb').patchValue(null);
    this.formGroup.get('unitNumber').patchValue(null);
    this.formGroup.get('fullAddress').patchValue(null);
    if (resetSearch) {
      this.formGroup.get('flatSearch').patchValue(null);
    }
  }

  populateStreetTypes() {
    return this.typesService.getTypesList('street-types-vx');
  }

  populateCountries() {
    return this.typesService.getTypesList('countries');
  }

  populateStates() {
    return this.typesService.getTypesList('australian-state-types');
  }

  populatePropertyTypes() {
    return this.typesService.getTypesList('property-types');
  }

  populateVxTypes() {
    return Promise.all([
      this.typesService.getTypesList('street-types-vx').then((t) => {
        this.vxStreetTypes = t;
      }),
      this.typesService.getTypesList('countries-vx').then((t) => {
        this.vxCountries = t;
      }),
      this.typesService.getTypesList('states-vx').then((t) => {
        this.vxStates = t;
        this.filteredStates = this.filterVXStates(this.formGroup.get('country').value);
        this.updateStateId(this.formGroup.get('state').value, true);
      })
    ]);
  }

  /**
   * populate all types
   */
  async populateTypes () {
    const populateAddressPromises = [
      this.populateStreetTypes(),
      this.populateCountries(),
      this.populateStates(),
      this.populatePropertyTypes(),
      this.populateVxTypes()
    ];

    await Promise.all(populateAddressPromises).then(([
      streets,
      countries,
      states,
      properties,
    ]) => {
      this.streetTypes = streets ? streets : [];
      this.countries = countries ? countries : [];
      this.auStates = states ? states : [];
      this.propertyTypes = properties || [];
    }).catch((err) => {
      console.log(err);
    });
  }

  /**
   * check if the selected country is AU country code.
   * @param country
   * @returns
   */
  public isAuCountryCode (country = this.formGroup.get("country")?.value) {
    return country === this.auCountryCode;
  }

  /**
   * update postcode validator based on the selected country.
   * @param country
   */
  public updatePostcodeValidation (country = this.formGroup.get('country').value) {
    // update only when `country` is not empty and defined.
    this.formGroup.get('postcode').clearAsyncValidators();
    if (country) {
      let validator = 'postcode';
      if(!this.isAuCountryCode(country)) {
        validator = this.nonAuPostCodeRequired ? 'nonAuPostcodeRequired' : 'nonAuPostcode'
      }
      this.formGroup.get('postcode').setAsyncValidators(fmcValidator(validator));
    }
    this.formGroup.get('postcode').updateValueAndValidity({ emitEvent: false });
  }

  /**
   * filter the `vxStates` based on the selected country.
   * @param country
   * @returns
   */
  public filterVXStates (country = this.formGroup.get('country').value) {
    return this.vxStates.filter(state => state.ext.CountryID === country);
  }

  /**
   * check if TBA is allowed, and flatSearch has a value of `TBA`.
   * @returns
   */
  public isTBA (flatSearch: string = this.formGroup.get('flatSearch').value) {
    return this.allowTba && flatSearch === 'TBA';
  }
}
